/**
 * Cart Vehicle Notes - Aggressive Interception
 * Automatically adds selected vehicle information to cart items
 * Uses multiple interception strategies to ensure vehicle data is always added
 */

(function() {
  'use strict';

  // ==========================================================================
  // HELPER FUNCTIONS
  // ==========================================================================

  // Get selected vehicle from localStorage
  function getSelectedVehicle() {
    try {
      const vehicleData = localStorage.getItem('selectedVehicle');
      if (!vehicleData) {
        return null;
      }
      const vehicle = JSON.parse(vehicleData);
      return vehicle;
    } catch (error) {
      return null;
    }
  }

  // Build properties object from vehicle data
  function getVehicleProperties(vehicle) {
    if (!vehicle) {
      return {};
    }

    const properties = {};

    if (vehicle.make) properties['Make'] = vehicle.make;
    if (vehicle.model) properties['Model'] = vehicle.model;
    if (vehicle.year) properties['Year'] = vehicle.year.toString();
    if (vehicle.trim) properties['Trim'] = vehicle.trim;
    if (vehicle.cabType) properties['Cab Type'] = vehicle.cabType;
    if (vehicle.registration) properties['Registration'] = vehicle.registration;

    return properties;
  }

  // Add vehicle properties to cart item(s)
  function addPropertiesToCartData(data, properties) {
    if (!properties || Object.keys(properties).length === 0) {
      return data;
    }

    // Handle bulk additions (items array)
    if (data.items && Array.isArray(data.items)) {
      data.items = data.items.map((item) => {
        return {
          ...item,
          properties: { ...(item.properties || {}), ...properties }
        };
      });
    } 
    // Handle single item addition
    else {
      data.properties = { ...(data.properties || {}), ...properties };
    }

    return data;
  }

  // Check if URL is a cart add endpoint
  function isCartAddUrl(url) {
    if (!url) return false;
    const urlStr = url.toString().toLowerCase();
    const isMatch = urlStr.includes('/cart/add.js') || 
                    urlStr.includes('/cart/add') ||
                    urlStr.endsWith('/cart/add.js') ||
                    urlStr.endsWith('/cart/add');
    return isMatch;
  }

  // ==========================================================================
  // INTERCEPTION 1: FETCH API
  // ==========================================================================

  const originalFetch = window.fetch;

  window.fetch = function(...args) {
    const [url, options = {}] = args;

    if (isCartAddUrl(url)) {
      const vehicle = getSelectedVehicle();
      
      if (vehicle) {
        const properties = getVehicleProperties(vehicle);
        
        if (Object.keys(properties).length > 0) {
          try {
            const modifiedOptions = { ...options };

            // Handle FormData bodies
            if (options.body instanceof FormData) {
              const newFormData = new FormData();
              
              // Copy all existing fields
              for (const [key, value] of options.body.entries()) {
                newFormData.append(key, value);
              }
              
              // Add vehicle properties as properties[key]
              Object.entries(properties).forEach(([key, value]) => {
                newFormData.append(`properties[${key}]`, value);
              });
              
              modifiedOptions.body = newFormData;
              return originalFetch.call(this, url, modifiedOptions);
            }
            
            // Handle JSON string bodies
            if (typeof options.body === 'string') {
              try {
                const bodyData = JSON.parse(options.body);
                const modifiedData = addPropertiesToCartData(bodyData, properties);
                modifiedOptions.body = JSON.stringify(modifiedData);
                
                if (!modifiedOptions.headers) {
                  modifiedOptions.headers = {};
                }
                if (typeof modifiedOptions.headers === 'object' && !Array.isArray(modifiedOptions.headers)) {
                  modifiedOptions.headers['Content-Type'] = 'application/json';
                }
                
                return originalFetch.call(this, url, modifiedOptions);
              } catch (e) {
                // If it's not JSON, it might be URLSearchParams - let it pass through
              }
            }
            
            // Handle plain object bodies
            if (typeof options.body === 'object' && options.body !== null && !(options.body instanceof FormData)) {
              const modifiedData = addPropertiesToCartData(options.body, properties);
              modifiedOptions.body = JSON.stringify(modifiedData);
              
              if (!modifiedOptions.headers) {
                modifiedOptions.headers = {};
              }
              if (typeof modifiedOptions.headers === 'object' && !Array.isArray(modifiedOptions.headers)) {
                modifiedOptions.headers['Content-Type'] = 'application/json';
              }
              
              return originalFetch.call(this, url, modifiedOptions);
            }
          } catch (error) {
            // Fall through to original request
          }
        }
      }
    }

    return originalFetch.apply(this, args);
  };

  // ==========================================================================
  // INTERCEPTION 2: XMLHttpRequest
  // ==========================================================================

  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._url = url;
    this._method = method;
    return originalXHROpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(body) {
    if (isCartAddUrl(this._url)) {
      const vehicle = getSelectedVehicle();
      
      if (vehicle) {
        const properties = getVehicleProperties(vehicle);
        
        if (Object.keys(properties).length > 0) {
          try {
            // Handle FormData bodies
            if (body instanceof FormData) {
              const newFormData = new FormData();
              
              // Copy all existing fields
              for (const [key, value] of body.entries()) {
                newFormData.append(key, value);
              }
              
              // Add vehicle properties as properties[key]
              Object.entries(properties).forEach(([key, value]) => {
                newFormData.append(`properties[${key}]`, value);
              });
              
              return originalXHRSend.call(this, newFormData);
            }
            
            // Handle JSON string bodies
            if (typeof body === 'string') {
              try {
                const bodyData = JSON.parse(body);
                const modifiedData = addPropertiesToCartData(bodyData, properties);
                const modifiedBody = JSON.stringify(modifiedData);
                return originalXHRSend.call(this, modifiedBody);
              } catch (e) {
                // If it's not JSON, let it pass through unchanged
              }
            }
            
            // Handle plain object bodies
            if (typeof body === 'object' && body !== null && !(body instanceof FormData)) {
              const modifiedData = addPropertiesToCartData(body, properties);
              const modifiedBody = JSON.stringify(modifiedData);
              return originalXHRSend.call(this, modifiedBody);
            }
          } catch (error) {
            // Fall through to original request
          }
        }
      }
    }

    return originalXHRSend.call(this, body);
  };

  // ==========================================================================
  // INTERCEPTION 3: FORM SUBMISSIONS
  // ==========================================================================

  function interceptFormSubmit(event) {
    const form = event.target;

    if (isCartAddUrl(form.action)) {
      const vehicle = getSelectedVehicle();
      
      if (vehicle) {
        const properties = getVehicleProperties(vehicle);
        
        if (Object.keys(properties).length > 0) {
          // Add vehicle properties as hidden inputs
          Object.entries(properties).forEach(([key, value]) => {
            const existingInput = form.querySelector(`input[name="properties[${key}]"]`);
            
            if (existingInput) {
              existingInput.value = value;
            } else {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = `properties[${key}]`;
              input.value = value;
              form.appendChild(input);
            }
          });
        }
      }
    }
  }

  // Listen for form submissions
  document.addEventListener('submit', interceptFormSubmit, true);

})();

